import React, { useCallback, useEffect, useRef, useState } from "react";

import ConfidenceReport from "./ConfidenceReport.jsx";
import { saveUploadedIos } from "../lib/localIos.js";

const BASE_URL = import.meta.env.VITE_API_URL || "/api/v1";
const API_KEY = import.meta.env.VITE_API_KEY || "";

const STAGE_LABELS = {
  uploading: "Uploading document",
  parsing: "Parsing document structure",
  extracting: "Extracting fields (LLM)",
  validating: "Validating & reconciling budget",
  done: "Done",
};

// Cap how large a file we'll inline into localStorage as a data: URL (for
// the "view original document" pane on Review). Bigger files just skip the
// inline preview — localStorage quota is small (~5-10MB per origin) and
// shared across every uploaded IO.
const MAX_INLINE_SOURCE_BYTES = 4 * 1024 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function UploadPage() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const savedJobIdRef = useRef(null);

  const reset = () => {
    clearInterval(pollRef.current);
    setJob(null);
    setError(null);
  };

  // Persist newly-extracted (non-mock) IOs to local storage exactly once per
  // completed job, so they show up on the Dashboard. There's no backend
  // "save draft" endpoint yet, so this is a stopgap client-only record.
  useEffect(() => {
    if (job?.status !== "done" || !job.result?.io_orders?.length) return;
    if (job.result._meta?.mock) return;
    if (savedJobIdRef.current === job.jobId) return;
    savedJobIdRef.current = job.jobId;

    const sourceFile = job.filename || file?.name;
    const extractedSections = job.result?.extractedSections || null;
    if (file && file.size <= MAX_INLINE_SOURCE_BYTES) {
      readFileAsDataUrl(file)
        .then((sourceDocDataUrl) => saveUploadedIos(job.result.io_orders, { sourceFile, sourceDocDataUrl, extractedSections }))
        .catch(() => saveUploadedIos(job.result.io_orders, { sourceFile, extractedSections }));
    } else {
      saveUploadedIos(job.result.io_orders, { sourceFile, extractedSections });
    }
  }, [job, file]);

  const startUpload = useCallback(async (chosenFile) => {
    reset();
    setFile(chosenFile);
    setError(null);

    const form = new FormData();
    form.append("file", chosenFile);

    try {
      const headers = {};
      if (API_KEY) headers["x-api-key"] = API_KEY;
      const res = await fetch(`${BASE_URL}/upload`, { method: "POST", body: form, headers });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload?.error?.message || `Upload failed (${res.status})`);

      const jobId = payload.data.jobId;
      setJob({ jobId, status: "processing", stages: [] });

      pollRef.current = setInterval(async () => {
        const statusRes = await fetch(`${BASE_URL}/upload/${jobId}`, { headers });
        const statusPayload = await statusRes.json();
        if (!statusRes.ok) {
          clearInterval(pollRef.current);
          setError(statusPayload?.error?.message || "Failed to fetch status");
          return;
        }
        setJob(statusPayload.data);
        // "done" and "error" are the terminal states. Guard with an explicit
        // whitelist rather than a blacklist so any future non-terminal status
        // still polls, and any terminal one always stops.
        const st = statusPayload.data.status;
        if (st === "done" || st === "error" || st === "duplicate") {
          clearInterval(pollRef.current);
        }
      }, 500);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) startUpload(dropped);
  };

  const onFileInput = (e) => {
    const chosen = e.target.files?.[0];
    if (chosen) startUpload(chosen);
  };

  return (
    <div className="upload-page">
      <div className="upload-left">
        <div
          className={`dropzone${dragOver ? " dropzone-active" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => document.getElementById("io-file-input").click()}
        >
          <input
            id="io-file-input"
            type="file"
            accept=".pdf,.docx,.xlsx,.csv,.html,.png,.jpg,.jpeg,.webp,.tiff,.tif,.bmp"
            style={{ display: "none" }}
            onChange={onFileInput}
          />
          <div className="dropzone-icon">📄</div>
          <p><strong>Drop an IO file here</strong> or click to browse</p>
          <p className="dropzone-hint">PDF, DOCX, XLSX, CSV, HTML, or a photo/scan of a handwritten IO (PNG, JPG, WEBP, TIFF, BMP)</p>
          {file && <p className="dropzone-filename">Selected: {file.name}</p>}
        </div>

        {error && <div className="upload-error">{error}</div>}

        {job?.status === "done" && job?.result?.duplicate && (
          <div className="upload-duplicate-notice">
            <strong>Already ingested</strong>
            <p>
              {job.result.message ||
                "Every IO in this file already exists in Longship. Nothing was created."}
            </p>
            {(job.result.duplicate_io_numbers || []).length > 0 && (
              <p className="upload-duplicate-ios mono">
                Existing IO{(job.result.duplicate_io_numbers || []).length === 1 ? "" : "s"}:{" "}
                {(job.result.duplicate_io_numbers || []).join(", ")}
              </p>
            )}
          </div>
        )}

        {job && (
          <ul className="progress-steps">
            {(job.stages?.length ? job.stages : Object.keys(STAGE_LABELS).map((key) => ({ key, label: STAGE_LABELS[key], status: "pending" }))).map(
              (stage) => (
                <li key={stage.key} className={`progress-step progress-step-${stage.status}`}>
                  <span className="progress-step-icon">
                    {stage.status === "done" ? "✅" : stage.status === "processing" ? "⏳" : "⚪"}
                  </span>
                  <span>{stage.label}</span>
                  {stage.mock && <span className="progress-step-mock">(mock)</span>}
                </li>
              )
            )}
          </ul>
        )}
      </div>

      <div className="upload-right">
        <div className="upload-right-header">
          <h3>
            Extracted JSON
            {job?.result?.order_count > 0 && (
              <span className="order-count-badge">
                {job.result.order_count} order{job.result.order_count === 1 ? "" : "s"} found
              </span>
            )}
          </h3>
          {job?.result?._meta?.mock && (
            <span className="mock-badge" title="Parser service or LLM extraction unavailable — showing a sample record">
              MOCK DATA
            </span>
          )}
        </div>
        <pre className="json-viewer">
          {job?.result
            ? JSON.stringify(job.result, null, 2)
            : job?.status === "error"
              ? `Error: ${job.error}`
              : job
                ? "Waiting for extraction to complete…"
                : "Upload a document to see the extracted campaign JSON here."}
        </pre>
      </div>

      <ConfidenceReport ioOrders={job?.result?.io_orders} />
    </div>
  );
}
